import { defineStore } from 'pinia';
import { io } from 'socket.io-client';
import http from '@/utils/http-common';
import router from '@/router';
import { speechrecognition } from '@/plugins/speechrecognition';
import emitter from '@/plugins/emitter';
const socket = io(import.meta.env.VITE_SERVER, {
  withCredentials: true,
  path: import.meta.env.VITE_SOCKET,
  transports: ['websocket', 'polling'],
});

const activeGameStatusses = [
  'startcountdown',
  'waitforanswer',
  'showfeedback',
  'nextplayer',
  'nextround',
  'endcountdown',
];

const labels = {
  name: { nl: 'speler naam', en: 'player name' },
  createGame: { nl: 'maak een nieuw spel', en: 'create a new game' },
  settings: { nl: 'spel instellingen', en: 'game settings' },
  lang: { nl: 'taal', en: 'language' },
  cont: { nl: 'continent(en)', en: 'continent(s)' },
  diff: { nl: 'moeilijkheidsgraad', en: 'difficulty' },
  start: { nl: 'start spel', en: 'start game' },
  startSolo: { nl: 'start solo spel', en: 'start solo game' },
  join: { nl: 'join', en: 'join' },
  online: { nl: 'aantal spelers online:', en: 'number of players online:' },
  placeholder: { nl: 'vul je alias in', en: 'enter your alias' },
  feedback: { nl: 'feedback', en: 'feedback' },
  end: { nl: 'game over', en: 'game over' },
  turn: { nl: 'aan de beurt', en: 'current player' },
  yourTurn: { nl: 'jouw beurt!', en: 'your turn!' },
  nextRound: { nl: 'volgende ronde', en: 'next question' },
  nextPlayer: { nl: 'volgende speler:', en: 'next player:' },
  notAnswered: {
    nl: 'niets gehoord',
    en: 'no speech detected',
  },
  nothingUsefulHeard: {
    nl: 'niets zinnigs gehoord: ',
    en: 'no useful speech detected:',
  },
  gameInstructions: {
    nl: 'kijk naar de vlag en zeg het antwoord hardop',
    en: 'look at the flag and say your answer out loud',
  },
  playAgain: { nl: 'speel nog een keer', en: 'play again' },
  leftTheGame: { nl: 'heeft het spel verlaten', en: 'has left the game' },
  speechAborted: {
    nl: 'speech afgebroken',
    en: 'speech aborted',
  },
  okLetsPlayAgain: {
    nl: 'laten we nog een keer spelen! wacht op andere spelers...',
    en: "let's play again! waiting for other players...",
  },
  waitForOtherPlayers: {
    nl: "wacht op andere spelers op 'ready' te klikken",
    en: 'waiting for other players to be ready',
  },
  waitForPlayers: {
    nl: "wacht op 'ready' van alle spelers...",
    en: 'waiting for all players to be ready...',
  },
  nowWaitOr: {
    nl: 'wacht op andere spelers of.....',
    en: 'now wait for other players or.....',
  },
  ImReady: { nl: 'Ik ben er klaar voor!', en: "I'm ready to play!" },
  shareUrl: {
    nl: 'deel deze url of qrcode met je vrienden om met ze te spelen',
    en: 'share this url or qrcode with your friends to play together',
  },
};

export const useFlagStore = defineStore('flagStore', {
  state: () => ({
    modalCount: 0,
    modals: {
      modalModel: {
        eventId: 'hier staat het eventId',
        title: 'Dit is een dummy titel',
        content: 'Hier staat een heel verhaal wat niet klopt',
        show: true,
        isNotification: true,
      },
      modalList: [],
    },
    speechSetupComplete: false, //speech is setup.
    speechPossible: false,
    isLoading: false, //is er een spinner
    isMounted: false, //is store gemount
    loadTimeOut: 0, // timer helper integer voor spinner
    gameStateInterval: null, //interval voor gamestate
    percentagePassed: 0, //percentage van de tijd die is verstreken
    timeOut: -1, //timer voor various gameStatus countdowns
    startTime: 0, //timer voor various gameStatus starts
    socket: null,
    connected: false, // is socket connected
    totalPlayersOnline: 0, //totaal aantal spelers online
    flags: [], //vlaggen in het spel
    languages: [], //talen in het spel
    difficulty: [], //moeilijkheidsgraden in het spel
    continentOptions: [], //continent opties in het spel
    lastPlayerName: '',
    gameUrlName: '',
    currentLanguageId: 'nl',
    currentGameStatus: null,
    finishedGame: null, //copy van game aan het einde van een spel
    game: {
      gameStatus: '',
      gameName: '',
      currentQuestion: 0,
      numberOfQuestions: 0,
      total: 0,
      players: [],
      activePlayer: -1,
    },
  }),
  getters: {
    //aparte finishedGame getter om te voorkomen dat de finishedGame in de store wordt aangepast => mensen verlaten het spel en dan wordt game overschreven maar fninishedGame niet
    getFinishedGame: (state) => {
      return state.finishedGame;
    },
    getGame: (state) => {
      return state.game;
    },
    getLabels() {
      return labels;
    },
    getLabel: (state) => {
      return (label) => {
        return labels[label][state.currentLanguageId];
      };
    },
    getFlags: (state) => {
      return state.flags;
    },
    getLanguages: (state) => {
      return state.languages;
    },
    getDifficulty: (state) => {
      return state.difficulty;
    },
    getContinentOptions: (state) => {
      return state.continentOptions;
    },
    getGameUrlName: (state) => {
      return state.gameUrlName;
    },
    getMyPlayer: (state) => {
      return state.game.players.find(
        (player) => player.socketId === state.socket.id,
      );
    },
    getMyFinishedPlayer: (state) => {
      return state.finishedGame.players.find(
        (player) => player.socketId === state.socket.id,
      );
    },
    getCurrentTimeOut: (state) => {
      return state.timeOut;
    },
    //of STT uberhaups mogelijk is.
    getSpeechPossibility: (state) => {
      return state.speechPossible;
    },
    //of STT is ingeschakeld
    getSpeechEnabled: () => {
      return speechrecognition.isEnabled;
    },
    getQuestion: (state) => {
      return (questionId) =>
        state.flags.find((question) => question.id === questionId);
    },
    //dit is om bij een backtohome etc de laatst ingevoerde playername te tonen.
    getLastPlayerName: (state) => {
      return state.lastPlayerName ? state.lastPlayerName : '';
    },
    getCurrentLanguageId: (state) => {
      return state.currentLanguageId;
    },
    getModalList: (state) => {
      return state.modals.modalList;
    },
    getErrorMessage: () => (err) => {
      // Convert the error object to a string for the alert
      let errorMessage = '';

      // Check if it's a standard Error object with message
      if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }
      // For DOMExceptions (common with audio/media errors)
      else if (err.name && err.name === 'DOMException') {
        errorMessage = `DOMException: ${err.message}`;
      }
      // Fall back to stringifying the object
      else {
        try {
          errorMessage = JSON.stringify(err, Object.getOwnPropertyNames(err));
        } catch (e) {
          errorMessage = "Error couldn't be fully serialized";
        }
      }
      return errorMessage;
    },
  },

  actions: {
    async mount() {
      this.showLoading(true);
      try {
        const response = await http.get('flags'); // Adjust the URL as necessary
        this.flags = response.data.flags;
        this.languages = response.data.languages;
        this.difficulty = response.data.difficulty;
        this.continentOptions = response.data.continentOptions;
      } catch (error) {
        console.error('Failed to fetch flags:', error);
      }
      await this.setSocket(socket);
      this.startInterval();
      let querystring = window.location.search.substring(1);
      //check if there's a gameName in the url
      this.showLoading(false);
      this.isMounted = true;
      socket.emit('client-ready');
      const gameName = querystring.split('=')[1];

      if (gameName && querystring.split('=')[0] === 'gameName') {
        this.gameUrlName = gameName;
        router.push({ name: 'JoinGamePage' });
      }
    },
    //of de browser stt ondersteunt
    setSpeechPossibility(bool) {
      this.speechPossible = bool;
    },
    async initAudio(lang) {
      if (this.speechSetupComplete) {
        return;
      }
      const me = this;
      // For iOS/mobile, it's best to initialize audio only on explicit user interaction
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      // On mobile, we might need a specific order or special handling
      if (isMobile) {
        // Return a promise that resolves when the user clicks OK
        await new Promise((resolve) => {
          emitter.on(`goAhead-proceed`, async () => {
            await me.initSpeech(lang);
            resolve(); // Resolve the promise when user confirms
          });

          this.setModal({
            title: '',
            content: 'Microphone test',
            eventName: 'goAhead',
            type: 'notification',
          });
        });
      } else {
        await this.initSpeech(lang);
      }
    },

    async initSpeech(lang) {
      try {
        this.showLoading(true);
        await speechrecognition.init(lang);
        this.showLoading(false);
        this.speechSetupComplete = true;
        if (!speechrecognition.isEnabled) {
          this.setModal({
            title: '',
            content:
              'Error setting up recognition. Try a different browser or device.',
            eventName: 'speechsetup',
            type: 'notification',
          });
        }
      } catch (err) {
        this.showLoading(false);
        if (
          !err.message.includes('Android platform') &&
          !err.message.includes('SpeechRecognition API not available')
        ) {
          this.setModal({
            title: '',
            content: `Setting up recognition: ${this.getErrorMessage(err)}`,
            eventName: 'microphoneError',
            type: 'notification',
          });
        }
      }
    },
    resetGameState() {
      this.gameUrlName = '';
      // this.finishedGame = null;
      this.currentGameStatus = null;
      this.timeOut = -1;
      this.percentagePassed = 0;
      this.game = {
        gameStatus: '',
        gameName: '',
        numberOfQuestions: 0,
        currentQuestion: 0,
        total: 0,
        players: [],
        activePlayer: -1,
      };
    },
    iWantToPlayAgain() {
      this.socket.emit('iWantToPlayAgain');
    },
    setCurrentLanguageId(languageId) {
      this.currentLanguageId = languageId;
    },
    setUpNewGame({ playerName, languageId, difficultyId, continentOption }) {
      this.showLoading(true);
      if (!this.socket.connected) {
        this.socket.connect();
      }
      this.socket.emit('joinGame', {
        gameName: 'newGame',
        playerName,
        languageId,
        difficultyId,
        continentOption,
      });
      this.lastPlayerName = playerName;
    },
    joinExistingGame({ playerName, languageId, gameName }) {
      if (!this.socket.connected) {
        this.socket.connect();
      }
      this.socket.emit('joinGame', {
        gameName,
        playerName,
        languageId,
      });
      this.currentLanguageId = languageId;
      this.lastPlayerName = playerName;
    },
    leaveThisGame() {
      if (this.socket) {
        this.socket.emit('leaveGame', this.gameName);
      }
    },
    readyToPlay() {
      this.socket.emit('readyToPlay');
    },
    disconnectSocket() {
      this.socket.disconnect();
    },
    sendAnswer({ transcript, confidence }) {
      this.socket.emit('answer', { transcript, confidence });
    },
    updateGame(game, percentage) {
      if (game.gameStatus !== this.currentGameStatus) {
        this.currentGameStatus = game.gameStatus;
        if (percentage) {
          this.startTime = Math.floor(Date.now() - percentage * game.timeOut);
        } else {
          this.startTime = Math.floor(Date.now());
        }
        const safetyMargin = 500;
        this.timeOut = game.timeOut ? game.timeOut - safetyMargin : -1;
        this.percentagePassed = 0;
      }
      this.game = game;
    },
    async setSocket(socket) {
      if (this.socket) {
        throw new Error('socket already set');
      }
      this.socket = socket;

      this.socket.on('connect', () => {
        this.connected = true;
      });

      this.socket.on('disconnect', () => {
        console.log('disconnect');
        this.connected = false;
        this.resetGameState();
        router.push({ name: 'HomePage' });
      });

      this.socket.on('reconnect', ({ game, percentage }) => {
        console.log('reconnect', game, percentage);

        //eerst checken of er niet een url is meegegeven want dan gebruiken we die als deze verschilt van het huidge spel!
        if (game && this.gameUrlName && game.gameName !== this.gameUrlName) {
          router.push({ name: 'JoinGamePage' });
          return;
        }
        this.updateGame(game, percentage);

        // player zetten
        const player = this.getMyPlayer;
        this.lastPlayerName = player.name;
        this.currentLanguageId = player.languageId;

        if (game.gameStatus === 'waitingforstart') {
          //nu zetten we finishedGame op null!
          this.finishedGame = null;
          router.push({ name: 'WaitForGameStartPage' });
          return;
        }

        if (game.gameStatus === 'gamerepeat' || game.gameStatus === 'gameend') {
          router.push({ name: 'GameEnd' });
          return;
        }

        if (activeGameStatusses.includes(game.gameStatus)) {
          router.push({ name: 'GamePage' });
        }
      });

      this.socket.on('connectionchange', (total) => {
        this.totalPlayersOnline = total;
      });

      this.socket.on('waitforgamestart', ({ game }) => {
        this.showLoading(false);
        this.updateGame(game);
        router.push({ name: 'WaitForGameStartPage' });
      });

      //niet gelukt om game te maken, te joinen, te .... iets
      this.socket.on('failedgame', (reason) => {
        console.log('failedgame', reason);
        this.resetGameState();
        router.push({ name: 'HomePage' });
      });

      this.socket.on('gamejoined', ({ game }) => {
        this.showLoading(false);
        console.log('gamejoined', game.gameName, game.players);
        this.updateGame(game);
        //
      });

      //vanuit de server wordt updategame alleen vanuit reconnect en ready to play, spelers online/offline, ready or not etc. komt hier binnen
      this.socket.on('updategame', ({ game }) => {
        this.updateGame(game);
      });

      //iedereen is ready. let's go play!
      this.socket.on('startcountdown', ({ game }) => {
        this.updateGame(game);
        router.push({ name: 'GamePage' });
      });

      //het spel wacht op een antwoord van een speler. er is verschil als het gaat om jouw antwoord dan wel een ander zijn antwoord.
      this.socket.on('waitforanswer', ({ game }) => {
        this.updateGame(game);
      });

      this.socket.on('getfinalanswer', ({ game }) => {
        this.updateGame(game);
      });

      /*het spel toont de feedback op een antwoord. er is verschil als het gaat om feedback op jouw antwoord dan wel feedback op een ander zijn antwoord.
      dit moet snel terug zijn!
      */
      this.socket.on('showfeedback', ({ game }) => {
        this.updateGame(game);
      });

      //het spel gaat naar de volgende ronde
      this.socket.on('nextround', ({ game }) => {
        console.log('nextround', game.gameName);
        this.updateGame(game);
      });

      // het spel toont wie er aan de beurt is.
      this.socket.on('nextplayer', ({ game }) => {
        console.log('nextplayer', game.gameName);
        this.updateGame(game);
      });

      //de eind countdown wordt getoond.
      this.socket.on('endcountdown', ({ game }) => {
        console.log('endcountdown', game.gameName);
        this.updateGame(game);

        //make a clone of this game and send it to the endscreen
        this.finishedGame = JSON.parse(JSON.stringify(game));
      });

      /*/*
'waitingforstart', //wait for players to join and readyup
'startcountdown', //show countdown to start the game
'waitforanswer', //wait for a player to answer
'getfinalanswer', // dit is voor spelers die eigenlijk uit de tijd lopen.
'showfeedback', //show feedback on the answer (correct/incorrect) or timeout
'nextround', //OPTIONAL1 after showfeedback and before nextplayer
'nextplayer', //show next player
'endcountdown',//show countdown when game has ended 
'gameend' //show endscreen with scores.*/

      /* if someone leaves the game then all other players get this message */
      this.socket.on('playerleft', ({ game, playerName }) => {
        this.setModal({
          content: playerName + ' ' + this.getLabel('leftTheGame'),
          title: '',
          eventName: 'playerleft',
          type: 'autonotification',
        });
        this.updateGame(game);
      });

      /* game enters gamerepeat phase */
      this.socket.on('gamerepeat', ({ game }) => {
        this.updateGame(game);
        router.push({ name: 'GameEnd' });
      });

      /* game enters gameend phase */
      this.socket.on('gameend', ({ game }) => {
        this.updateGame(game);
        router.push({ name: 'GameEnd' });
      });

      /* the players who are still in the game get this event */
      this.socket.on('playerleft', ({ game, playerName }) => {
        console.log('playerleft', playerName);
        //todo notify!
        this.updateGame(game);
        //no route change
      });

      /* the player who left a game gets this event */
      this.socket.on('leftTheGame', () => {
        console.log('you leftTheGame', this.getGameUrlName);
        this.resetGameState();
        router.push({ name: 'HomePage' });
      });
    },
    startInterval() {
      this.gameStateInterval = setInterval(() => {
        //slot op de deur want server kan wat trager zijn waardoor % > 100 kan worden
        if (this.timeOut != -1 && this.percentagePassed <= 100) {
          const currentTime = Math.floor(Date.now());
          const timePassed = currentTime - this.startTime;

          this.percentagePassed = Math.floor((100 * timePassed) / this.timeOut);
          if (this.percentagePassed > 100) {
            this.percentagePassed = 100;
          }
        } else {
          this.percentagePassed = 0;
        }
      }, 100);
    },

    setModal({ content, title, eventName, type }) {
      let modal = Object.assign(this.modals.modalModel);
      modal.content = content;
      modal.title = title;
      modal.eventName = eventName;
      modal.type = type;
      modal.id = this.modalCount++;
      if (
        !this.modals.modalList.find((modal) => modal.eventName === eventName)
      ) {
        this.modals.modalList.push(modal);
      } else {
        //  console.log('skipped adding : ' + eventName)
      }
    },
    closeModal(modalId) {
      const index = this.modals.modalList.findIndex((modal) => {
        return modal.id == modalId;
      });
      if (index != -1) {
        this.modals.modalList.splice(index, 1);
      }
    },
    showLoading(bool) {
      clearTimeout(this.loadTimeOut);
      if (bool) {
        this.loadTimeOut = window.setTimeout(() => {
          this.isLoading = true;
        }, 500);
      } else {
        clearTimeout(this.loadTimeOut);
        this.isLoading = false;
      }
    },
  },
});
